/**
 * HMRC API Service
 * Handles all HMRC Making Tax Digital API calls
 */

import { apiClient } from '../client';
import type { VATObligation, VATReturn } from '../../types/api';

export const hmrcApi = {
  // ============================================================================
  // OAuth2 Authentication
  // ============================================================================

  /**
   * Get OAuth status
   */
  getOAuthStatus: async (): Promise<any> => {
    const response = await apiClient.get('/hmrc/oauth/status');
    return response.data;
  },

  /**
   * Initiate OAuth authorization
   */
  initiateAuthorization: async (userId?: string, redirectUrl?: string): Promise<{ authorization_url: string; state: string }> => {
    const response = await apiClient.post('/hmrc/oauth/authorize', null, {
      params: { user_id: userId, redirect_url: redirectUrl },
    });
    return response.data;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (userId: string): Promise<any> => {
    const response = await apiClient.post('/hmrc/oauth/refresh', null, {
      params: { user_id: userId },
    });
    return response.data;
  },

  /**
   * Revoke access token
   */
  revokeToken: async (userId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/hmrc/oauth/revoke', null, {
      params: { user_id: userId },
    });
    return response.data;
  },

  /**
   * Get token information
   */
  getTokenInfo: async (userId: string): Promise<any> => {
    const response = await apiClient.get('/hmrc/oauth/token-info', {
      params: { user_id: userId },
    });
    return response.data;
  },

  // ============================================================================
  // VAT API
  // ============================================================================

  /**
   * Get VAT obligations
   */
  getVATObligations: async (userId: string, vrn: string, fromDate?: string, toDate?: string): Promise<{ obligations: VATObligation[] }> => {
    const response = await apiClient.get('/hmrc/vat/obligations', {
      params: { user_id: userId, vrn, from_date: fromDate, to_date: toDate },
    });
    return response.data;
  },

  /**
   * Submit VAT return
   */
  submitVATReturn: async (userId: string, vatReturn: VATReturn): Promise<any> => {
    const response = await apiClient.post('/hmrc/vat/returns', vatReturn, {
      params: { user_id: userId },
    });
    return response.data;
  },

  /**
   * Get VAT return by period key
   */
  getVATReturn: async (periodKey: string, userId: string, vrn: string): Promise<VATReturn> => {
    const response = await apiClient.get<VATReturn>(`/hmrc/vat/returns/${periodKey}`, {
      params: { user_id: userId, vrn },
    });
    return response.data;
  },

  /**
   * Get VAT liabilities
   */
  getVATLiabilities: async (userId: string, vrn: string, fromDate?: string, toDate?: string): Promise<any> => {
    const response = await apiClient.get('/hmrc/vat/liabilities', {
      params: { user_id: userId, vrn, from_date: fromDate, to_date: toDate },
    });
    return response.data;
  },

  /**
   * Get VAT payments
   */
  getVATPayments: async (userId: string, vrn: string, fromDate?: string, toDate?: string): Promise<any> => {
    const response = await apiClient.get('/hmrc/vat/payments', {
      params: { user_id: userId, vrn, from_date: fromDate, to_date: toDate },
    });
    return response.data;
  },

  // ============================================================================
  // Test User (Sandbox)
  // ============================================================================

  /**
   * Create HMRC test user (sandbox only)
   */
  createTestUser: async (): Promise<any> => {
    const response = await apiClient.post('/hmrc/test-user/create');
    return response.data;
  },

  // ============================================================================
  // Payment Initiation
  // ============================================================================

  /**
   * Initiate payment journey (Self Assessment or VAT)
   */
  initiatePayment: async (
    taxRegime: 'SelfAssessment' | 'Vat',
    reference: string,
    amountInPence: number,
    backUrl?: string
  ): Promise<{
    success: boolean;
    redirect_url: string;
    client_journey_id: string;
    message: string;
  }> => {
    const response = await apiClient.post('/hmrc/payments', {
      tax_regime: taxRegime,
      reference,
      amount_in_pence: amountInPence,
      back_url: backUrl,
      friendly_name: 'EF-CapFlow',
    });
    return response.data;
  },

  /**
   * Check payment journey status
   */
  getPaymentStatus: async (clientJourneyId: string): Promise<{
    success: boolean;
    client_journey_id: string;
    tax_regime: string;
    amount_in_pence: number;
    payment_status: 'Completed' | 'Failed' | 'InProgress';
  }> => {
    const response = await apiClient.get('/hmrc/payments/status', {
      params: { client_journey_id: clientJourneyId },
    });
    return response.data;
  },
};

export default hmrcApi;
